import * as Sentry from '@sentry/react';

export const AUTH0_CACHE_PREFIX = '@@auth0spajs@@';

export function getCachedAuth0RefreshTokens(): string[] {
  try {
    const tokens = new Set<string>();
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(AUTH0_CACHE_PREFIX)) continue;

      // Per-entry so one malformed blob can't stop us revoking the rest.
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const refreshToken = JSON.parse(raw)?.body?.refresh_token;
        if (typeof refreshToken === 'string' && refreshToken) {
          tokens.add(refreshToken);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'getCachedAuth0RefreshTokens: entry', key },
        });
      }
    }
    return [...tokens];
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'getCachedAuth0RefreshTokens' },
    });
    return [];
  }
}

// The Auth0 SPA SDK deletes the refresh token from localStorage on logout but
// never revokes the grant server-side, so a copied token stays valid until its
// own expiry. Revoke it explicitly. Fire-and-forget with keepalive so the
// request survives the immediate /v2/logout redirect.
export function revokeAuth0RefreshTokens(): void {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  if (!domain || !clientId) return;

  for (const token of getCachedAuth0RefreshTokens()) {
    // Auth0 IdP endpoint (public client — no secret, no bearer header), not our
    // platform API, so this intentionally bypasses apiRequest.
    fetch(`https://${domain}/oauth/revoke`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: clientId, token }),
    }).catch((error) => {
      Sentry.captureException(error, {
        extra: { context: 'revokeAuth0RefreshTokens' },
      });
    });
  }
}

// Must run AFTER revokeAuth0RefreshTokens() — this is what destroys the token
// that revocation needs to send.
export function clearAuth0Cache(): void {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(AUTH0_CACHE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    Sentry.captureException(error, { extra: { context: 'clearAuth0Cache' } });
  }
}
